import React from "react";
import { TableRow, Box, Typography, Button } from "@material-ui/core";
import numeral from "numeral";
import TableCell from "./TableCell";
import { useJVTransInv } from "../JVTransInv";
import CostDetails from "src/components/CostDetails";
import PhantomDetails from "./PhantomDetails";
import useStyles from "../useStyles";
import { useToggle } from "src/hooks";
import { INVENTORY_TYPES } from "src/constants";

const Details = ({ caption, value, align = "right" }) => {
  return (
    <Box display="flex" gridGap="2em" alignItems="center">
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ fontWeight: 600, flex: "0 0 10em" }}
      >
        {caption} :
      </Typography>
      <Typography
        variant="body2"
        align={align}
        style={{
          flex: 1
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const MarkUpDetails = ({ items, muDescription }) => {
  return (
    <Box minWidth={350}>
      <Box mb={2} display="flex">
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {muDescription}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
        <Details caption={muDescription} value={items.muDesc} />
        <Details
          caption="Sub-total"
          value={numeral(items.muSubTotal).format("0,0.00")}
        />
      </Box>
    </Box>
  );
};

const DiscOutrightDetails = ({ items, discOutrightDesc = "" }) => {
  return (
    <Box minWidth={350}>
      <Box mb={2} display="flex">
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {discOutrightDesc}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
        <Details caption={discOutrightDesc} value={items.discOutrightDesc} />
        <Details
          caption="Sub-total"
          value={numeral(items.discOutrightSubTotal).format("0,0.00")}
        />
      </Box>
    </Box>
  );
};

const DiscDetails = ({ items, discountDesc = "" }) => {
  return (
    <Box minWidth={350}>
      <Box mb={2} display="flex">
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {discountDesc}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
        <Details caption={discountDesc} value={items.discDesc} />
        <Details
          caption="Sub-total"
          value={numeral(items.discSubTotal).format("0,0.00")}
        />
      </Box>
    </Box>
  );
};

function RowDetails({ showDetails, ...rest }) {
  const classes = useStyles();
  const [costDetails, showCostDetails, hideCostDetails] = useToggle();
  const [phantomDetails, showPhantomDetails, hidePhantomDetails] = useToggle();
  const {
    showCost,
    MARK_UP,
    DISC_OUTRIGHT,
    DISC,
    WAREHOUSES,
    INV_TYPE
  } = useJVTransInv();

  if (!showDetails) return null;

  return (
    <TableRow className={classes.details}>
      <TableCell colSpan="100%">
        <Box display="flex" gridGap="1em">
          {[
            INVENTORY_TYPES.PHANTOM_SALES,
            INVENTORY_TYPES.STOCK_ADJUSTMENT_BY_VARIANCE,
            INVENTORY_TYPES
          ].includes(INV_TYPE) && (
            <Button
              color="primary"
              tabIndex={-1}
              size="small"
              onClick={showPhantomDetails}
            >
              View Items
            </Button>
          )}
          {showCost && (
            <>
              <Button
                color="primary"
                tabIndex={-1}
                size="small"
                onClick={showCostDetails}
              >
                View Cost Details
              </Button>
              <Box display="flex" alignItems="center" ml={1}>
              <Typography variant="body2" color="textSecondary">
                PRODUCT ID: <span style={{ fontWeight: 'bold' }}>{rest.ixProd}</span>
              </Typography>
            </Box>
            </>
          )}
          {showCost && (
            <CostDetails
              open={costDetails}
              handleClose={hideCostDetails}
              productDetails={rest}
              pixDetails={rest?._pix_details ?? []}
              warehouseList={WAREHOUSES}
            />
          )}

          <PhantomDetails
            detail_items={rest?.detail_items ?? []}
            open={phantomDetails}
            onClose={hidePhantomDetails}
            invType={INV_TYPE}
          />
        </Box>
        {(MARK_UP.allow || DISC_OUTRIGHT.allow || DISC.allow) && (
          <Box display="flex" gridGap="3em" mt={3}>
            {MARK_UP.allow && (
              <MarkUpDetails items={rest} muDescription={MARK_UP.description} />
            )}

            {DISC_OUTRIGHT.allow && (
              <DiscOutrightDetails
                items={rest}
                discOutrightDesc={DISC_OUTRIGHT.description}
              />
            )}

            {DISC.allow && (
              <DiscDetails items={rest} discountDesc={DISC.description} />
            )}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}

export default RowDetails;
